import React, { useMemo, useState } from 'react';
import { Note, User } from '../types';
import { NoteComposer } from '../components/notes/NoteComposer';
import { NotesFeed } from '../components/notes/NotesFeed';
import { NotesBoardView } from '../components/notes/NotesBoardView';
import { NotesFilters } from '../components/notes/NotesFilters';
import { Modal } from '../components/ui/Modal';
import { Badge } from '../components/ui/Badge';
import { FileText, MapPin, Percent, Home } from 'lucide-react';
const TEAM_MEMBERS = [{
  id: 'u1',
  name: 'Alex Rivera',
  role: 'Loan Officer',
  initials: 'AR'
}, {
  id: 'u2',
  name: 'System',
  role: 'Automated',
  initials: 'SYS'
}, {
  id: 'u3',
  name: 'Sarah Chen',
  role: 'Underwriter',
  initials: 'SC'
}, {
  id: 'u4',
  name: 'David Park',
  role: 'Processor',
  initials: 'DP'
}, {
  id: 'u5',
  name: 'Jennifer Torres',
  role: 'Compliance Officer',
  initials: 'JT'
}];
const MOCK_LOAN = {
  id: 'LN-2024-8492',
  borrowerName: 'Sarah & Michael Jenkins',
  amount: 450000,
  status: 'Underwriting',
  address: '123 Maple Avenue, Springfield, IL 62704',
  rate: 6.875,
  loanType: '30 Year Fixed'
};
const MOCK_NOTES: Note[] = [{
  id: '1',
  subject: 'Income Verification Pending',
  content: '<p>Still waiting on the W2s for 2023 from Michael. I called him today and he said he would upload them by EOD.</p>',
  createdAt: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
  author: {
    id: 'u1',
    name: 'Alex Rivera',
    role: 'Loan Officer',
    initials: 'AR'
  },
  category: 'Processing',
  isPrivate: false
}, {
  id: '2',
  subject: 'Appraisal Ordered',
  content: '<p>Appraisal has been ordered through AMC. Expected turnaround time is 5 business days due to high volume in the area.</p><ul><li>Vendor: ClearCapital</li><li>Cost: $650</li></ul>',
  createdAt: new Date(Date.now() - 1000 * 60 * 60 * 4).toISOString(),
  author: {
    id: 'u2',
    name: 'System',
    role: 'Automated',
    initials: 'SYS'
  },
  category: 'General',
  isPrivate: false
}, {
  id: '3',
  subject: 'Internal Review: DTI Concerns',
  content: '<p>The DTI is tight at 44%. We need to make sure we exclude the student loans that are being paid by the parents. Please get documentation for the last 12 months of payments.</p>',
  createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
  author: {
    id: 'u3',
    name: 'Sarah Chen',
    role: 'Underwriter',
    initials: 'SC'
  },
  category: 'Underwriting',
  isPrivate: false
}, {
  id: '4',
  subject: 'Title Search Complete',
  content: '<p>Title search came back clean. No liens or judgments found on the property.</p>',
  createdAt: new Date(Date.now() - 1000 * 60 * 60 * 48).toISOString(),
  author: {
    id: 'u4',
    name: 'David Park',
    role: 'Processor',
    initials: 'DP'
  },
  category: 'Processing',
  isPrivate: false
}, {
  id: '5',
  subject: 'Client Call Summary',
  content: '<p>Spoke with Sarah regarding the insurance binder. She will have her agent send it over by tomorrow morning.</p>',
  createdAt: new Date(Date.now() - 1000 * 60 * 60 * 72).toISOString(),
  author: {
    id: 'u1',
    name: 'Alex Rivera',
    role: 'Loan Officer',
    initials: 'AR'
  },
  category: 'Call',
  isPrivate: false
}, {
  id: '6',
  subject: 'Rate Lock Confirmed',
  content: '<p>Rate lock confirmed at <strong>6.875%</strong> for 45 days. Lock expiration: March 15, 2025. Please ensure closing is scheduled before this date.</p>',
  createdAt: new Date(Date.now() - 1000 * 60 * 60 * 96).toISOString(),
  author: {
    id: 'u1',
    name: 'Alex Rivera',
    role: 'Loan Officer',
    initials: 'AR'
  },
  category: 'General',
  isPrivate: false
}, {
  id: '7',
  subject: 'Compliance Review Complete',
  content: '<p>All compliance checks passed. TRID disclosures sent and acknowledged by borrowers on Feb 10.</p>',
  createdAt: new Date(Date.now() - 1000 * 60 * 60 * 120).toISOString(),
  author: {
    id: 'u5',
    name: 'Jennifer Torres',
    role: 'Compliance Officer',
    initials: 'JT'
  },
  category: 'Closing',
  isPrivate: false
}];
export function LoanNotesPage() {
  const [notes, setNotes] = useState<Note[]>(MOCK_NOTES);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDateRange, setSelectedDateRange] = useState('all');
  const [viewMode, setViewMode] = useState<'list' | 'board'>('list');
  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const uniqueUsers = useMemo(() => Array.from(new Map(notes.map(n => [n.author.id, n.author])).values()), [notes]);
  const handleSaveNote = (newNoteData: any) => {
    const newNote: Note = {
      id: Date.now().toString(),
      ...newNoteData,
      createdAt: new Date().toISOString(),
      author: {
        id: 'me',
        name: 'You',
        role: 'Loan Officer',
        initials: 'ME'
      },
      replies: []
    };
    setNotes([newNote, ...notes]);
    setIsComposerOpen(false);
  };
  const handleReply = (parentId: string, content: string, taggedUsers?: User[]) => {
    const reply: Note = {
      id: `reply-${Date.now()}`,
      subject: `Re: Note ${parentId}`,
      content,
      createdAt: new Date().toISOString(),
      author: {
        id: 'me',
        name: 'You',
        role: 'Loan Officer',
        initials: 'ME'
      },
      category: 'General',
      isPrivate: false,
      taggedUsers
    };
    setNotes(prevNotes => prevNotes.map(note => {
      if (note.id === parentId) {
        return {
          ...note,
          replies: [...(note.replies || []), reply]
        };
      }
      return note;
    }));
  };
  const handleDelete = (noteId: string) => {
    setNotes(prevNotes => prevNotes.filter(note => note.id !== noteId));
  };
  const filteredNotes = useMemo(() => notes.filter(note => {
    const matchesSearch = note.subject.toLowerCase().includes(searchQuery.toLowerCase()) || note.content.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesUser = selectedUser === 'all' || note.author.id === selectedUser;
    const matchesCategory = selectedCategory === 'all' || note.category === selectedCategory;
    let matchesDate = true;
    if (selectedDateRange !== 'all') {
      const noteDate = new Date(note.createdAt);
      const now = new Date();
      const diffMs = now.getTime() - noteDate.getTime();
      const diffDays = diffMs / (1000 * 60 * 60 * 24);
      switch (selectedDateRange) {
        case 'today':
          matchesDate = diffDays <= 1;
          break;
        case 'week':
          matchesDate = diffDays <= 7;
          break;
        case 'month':
          matchesDate = diffDays <= 30;
          break;
        case '90days':
          matchesDate = diffDays <= 90;
          break;
      }
    }
    return matchesSearch && matchesUser && matchesCategory && matchesDate;
  }), [notes, searchQuery, selectedUser, selectedCategory, selectedDateRange]);
  const activeFilterCount = [selectedUser !== 'all', selectedCategory !== 'all', selectedDateRange !== 'all', searchQuery !== ''].filter(Boolean).length;
  return <div className="min-h-screen bg-slate-50" data-id="element-284">
      {/* Main Content */}
      <main className="max-w-6xl mx-auto w-full px-4 md:px-6 py-6" data-id="element-285">
        <NotesFilters onSearch={setSearchQuery} onUserFilter={setSelectedUser} onDateFilter={setSelectedDateRange} onCategoryFilter={setSelectedCategory} viewMode={viewMode} onViewChange={setViewMode} users={uniqueUsers} selectedUser={selectedUser} selectedCategory={selectedCategory} selectedDateRange={selectedDateRange} onNewNote={() => setIsComposerOpen(true)} data-id="element-286" />

        {/* Results count */}
        {activeFilterCount > 0 && <div className="flex items-center justify-between mb-4" data-id="element-287">
            <p className="text-sm text-slate-500" data-id="element-288">
              Showing{' '}
              <span className="font-semibold text-slate-700" data-id="element-289">
                {filteredNotes.length}
              </span>{' '}
              of {notes.length} notes
            </p>
            <button onClick={() => {
          setSearchQuery('');
          setSelectedUser('all');
          setSelectedCategory('all');
          setSelectedDateRange('all');
        }} className="text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors" data-id="element-290">
              Clear all filters
            </button>
          </div>}

        {viewMode === 'list' ? <NotesFeed notes={filteredNotes} onReply={handleReply} onDelete={handleDelete} availableUsers={TEAM_MEMBERS} data-id="element-291" /> : <NotesBoardView notes={filteredNotes} onReply={handleReply} availableUsers={TEAM_MEMBERS} data-id="element-292" />}
      </main>

      {/* New Note Modal */}
      <Modal isOpen={isComposerOpen} onClose={() => setIsComposerOpen(false)} title={`New Note — ${MOCK_LOAN.id}`} maxWidth="max-w-4xl" data-id="element-293">
        <NoteComposer onSave={handleSaveNote} onCancel={() => setIsComposerOpen(false)} defaultExpanded={true} availableUsers={TEAM_MEMBERS} data-id="element-294" />
      </Modal>
    </div>;
}